// Context menus and dropdowns. See docs/framework/ui/menus.md.

import { readonly, shallowRef, type Ref } from 'vue'

export interface MenuItem {
  label?:     string
  action?:    () => void | Promise<void>
  kind?:      'default' | 'danger'
  disabled?:  boolean
  icon?:      string
  separator?: boolean
  submenu?:   MenuItem[]
}

export type MenuAnchor =
  | { kind: 'point';   x: number; y: number }
  | { kind: 'element'; el: HTMLElement }

export interface ActiveMenu {
  id:     number
  items:  MenuItem[]
  anchor: MenuAnchor
  /** Optional footer text (e.g., app version in the account menu). */
  footer?: string
}

// shallowRef so nested props (notably anchor.el) preserve object identity.
// The whole ActiveMenu is always replaced, never mutated in place, so we don't
// need deep reactivity — and deep reactivity actively breaks the toggle check
// below by proxying the anchor element and losing reference equality.
const active = shallowRef<ActiveMenu | null>(null)
let nextId = 1

/**
 * Open a menu. When called with a MouseEvent, suppresses the browser's
 * native context menu automatically. When called with the same element
 * anchor as the currently-open menu, closes instead — pairs with the
 * ContextMenuLayer's anchor-aware click-outside handling to give trigger
 * buttons natural toggle behaviour.
 */
export function openMenu(
  from: MouseEvent | HTMLElement,
  items: MenuItem[],
  opts?: { footer?: string },
): void {
  // Toggle: a click on the same element that already anchors an open menu
  // closes it, rather than reopening a fresh one.
  const current = active.value
  if (current && current.anchor.kind === 'element' && 'nodeType' in from && current.anchor.el === from) {
    active.value = null
    return
  }

  const anchor: MenuAnchor =
    'nodeType' in from
      ? { kind: 'element', el: from }
      : (from.preventDefault(), { kind: 'point', x: from.clientX, y: from.clientY })

  active.value = { id: nextId++, items, anchor, footer: opts?.footer }
}

/** Close whichever menu (if any) is currently open. */
export function closeMenu(): void {
  active.value = null
}

/** Read the active menu. Consumed by ContextMenuLayer; leaf code has no need. */
export function useContextMenu(): { active: Readonly<Ref<ActiveMenu | null>> } {
  return { active: readonly(active) as Readonly<Ref<ActiveMenu | null>> }
}
